import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import UrlStep from "@/components/generate/UrlStep";
import BriefStep from "@/components/generate/BriefStep";
import ProgressPanel, { STAGE_COUNT } from "@/components/generate/ProgressPanel";
import { api, getSessionId } from "@/lib/api";

const GOALS = ["More qualified leads", "Foot traffic / walk-ins", "Online sales", "Brand awareness", "Appointment bookings"];

function briefFromInference(raw) {
  return {
    business_name:   raw.business_name || "",
    business_type:   raw.business_type || "",
    location:        raw.location || "",
    product_service: raw.product_service || "",
    target_customer: raw.target_customer || "",
    monthly_budget:  String(raw.monthly_budget || "20000"),
    primary_goal:    raw.primary_goal || GOALS[0],
    competitors:     raw.competitors || "",
    usp:             raw.usp || "",
    brand_tone:      raw.brand_tone || "Professional",
    inference_notes: raw.inference_notes || "",
  };
}

function payloadFromBrief(brief, snapshot, url, model) {
  return {
    session_id: getSessionId(),
    business_name: brief.business_name,
    business_type: brief.business_type,
    location: brief.location,
    product_service: brief.product_service,
    target_customer: brief.target_customer,
    monthly_budget: brief.monthly_budget,
    primary_goal: brief.primary_goal,
    channels: ["google", "meta"],
    competitors: brief.competitors,
    usp: brief.usp,
    brand_tone: brief.brand_tone,
    website_url: snapshot?.url || url.trim(),
    model,
  };
}

async function pollJob(jobId, timeoutMs = 6 * 60 * 1000) {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    await new Promise((r) => setTimeout(r, 4000));
    const { data: job } = await api.get(`/plans/jobs/${jobId}`);
    if (job.status === "done" && job.plan_id) return job.plan_id;
    if (job.status === "error") throw new Error(job.error || "Generation failed");
  }
  throw new Error("Timed out waiting for the plan. Please try again.");
}

export default function Generate() {
  const navigate = useNavigate();
  const [stepName, setStepName] = useState("url"); // url | brief | building
  const [url, setUrl] = useState("");
  const [model, setModel] = useState("claude");
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState(0);
  const [brief, setBrief] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const updateBrief = (k, v) => setBrief((b) => ({ ...b, [k]: v }));

  const analyzeUrl = async () => {
    if (!url.trim()) { toast.error("Paste your website URL to begin"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/website/infer-brief", { url: url.trim(), model });
      setBrief(briefFromInference(data.brief));
      setSnapshot(data.website_snapshot);
      setStepName("brief");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read that site. Try another URL.");
    } finally {
      setBusy(false);
    }
  };

  const buildPlan = async () => {
    if (!brief.business_name || !brief.business_type) {
      toast.error("Business name and type are required");
      return;
    }
    setStepName("building");
    setStage(0);
    setBusy(true);
    const timer = setInterval(() => {
      setStage((s) => (s < STAGE_COUNT - 1 ? s + 1 : s));
    }, 12000);
    try {
      const { data } = await api.post(
        "/plans/generate-async",
        payloadFromBrief(brief, snapshot, url, model),
      );
      const planId = await pollJob(data.job_id);
      clearInterval(timer);
      toast.success("Your next moves are ready!");
      navigate(`/plan/${planId}`);
    } catch (e) {
      clearInterval(timer);
      setStepName("brief");
      toast.error(e?.response?.data?.detail || e?.message || "Could not generate plan. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <div className="max-w-3xl mx-auto px-6 py-16">
        {stepName === "url" && (
          <UrlStep url={url} setUrl={setUrl} model={model} setModel={setModel} busy={busy} onAnalyze={analyzeUrl} />
        )}
        {stepName === "brief" && brief && (
          <BriefStep
            brief={brief}
            snapshot={snapshot}
            updateBrief={updateBrief}
            onBack={() => setStepName("url")}
            onBuild={buildPlan}
            busy={busy}
          />
        )}
        {stepName === "building" && <ProgressPanel stage={stage} url={url} />}
      </div>
      <Footer />
    </div>
  );
}
